import type { DataTableSort } from '#components/elements/dataTable/dataTable.model';
import type { SortType } from '#domain/performance/kpi/kpi.query';
import { type DataTableColumn, type DataTableKpiDisplayNameType, type DataTableRow } from '@carrefour-gcs/shared';
import { computed, ref, type Ref, watch } from 'vue';

interface UseDataTableSortParams {
  dataTableColumns: Ref<DataTableColumn<DataTableRow>[] | undefined>;
}

export function useDataTableSort(params: UseDataTableSortParams) {
  const { dataTableColumns } = params;

  const dataTableSort = ref<DataTableSort<DataTableKpiDisplayNameType>>();

  const sortedColumn = computed(() => {
    return dataTableSort.value
      ? dataTableColumns.value?.find((column) => column.name === dataTableSort.value?.columnId)
      : undefined;
  });

  const sortQueryParams = computed<SortType | undefined>(() => {
    if (!sortedColumn.value?.sort?.length || !dataTableSort.value) {
      const firstSortableColumn = dataTableColumns.value?.find((column) => column.isSortable && column.sort?.length);
      const firstSort = firstSortableColumn?.sort?.[0];

      return firstSortableColumn && firstSort ? [{ field: firstSort, direction: 'desc' }] : undefined;
    }

    const firstSort = sortedColumn.value.sort[0];
    return firstSort ? [{ field: firstSort, direction: dataTableSort.value.direction }] : undefined;
  });

  const isSorted = computed(() => !!sortQueryParams.value);

  watch(
    [dataTableColumns, dataTableSort],
    () => {
      if (dataTableColumns.value) {
        const defaultSortedColumnId = dataTableColumns.value.find(
          (column) => column.isSortable && column.type === 'kpi' && column.sort?.length
        )?.name as DataTableKpiDisplayNameType;

        if (!dataTableSort.value) {
          dataTableSort.value = { columnId: defaultSortedColumnId, direction: 'desc' };
        } else if (!dataTableColumns.value.find((column) => column.name === dataTableSort.value!.columnId)) {
          dataTableSort.value.columnId = defaultSortedColumnId;
        }
      }
    },
    { immediate: true }
  );

  return {
    dataTableSort,
    isSorted,
    sortQueryParams
  };
}
